import { DataIndexItem, OwnerDetails } from "../../../types/interfaces"
import { Vector3, Matrix4 } from "three"
import { ConversionStatus } from "../../../common/constant"
import { message } from "antd"
import { getIdToken } from "../../userManager/userManagementUtils"
import { handleResponse } from "../../../common/handleLambdaResponse"
import { addDataItemsStore, addOwnerDetailsStore, addOwnerIdsStore } from "../../../store/DataStore"
import { api_trigger_fxn } from "../../../utils/APIs/ApiUtils"

export const addItemsInBatch = (inBatchId: string | null, inDataIds: string[], inOwner : string, inName : string | null) => {
    return new Promise(async (resolve, reject) => {
        let idToken;
        try {
            idToken = await getIdToken();
        } catch (err) {
            message.error("Failed to add items to batch")
            reject(err);
        }
        const payload : any = {
            owner_id: inOwner,
            action: "add_items_to_batch",
            data_ids: inDataIds,
        }

        if(!inBatchId && !inName) {
            reject("Batch ID or Name is required")
        }

        if(inBatchId) {
            payload["batch_id"] = inBatchId
        }
        if(inName) {
            payload["batch_name"] = inName
        }

        let body: { added_ids: any[] };
        try {
            body = await api_trigger_fxn("data_index", payload);
        } catch (e) {
            return reject(e);
        }
        resolve(body);
    })
}

export const removeItemsFromBatch = (inBatchId: string, inDataIds: string[], inOwner : string) => {
    return new Promise(async (resolve, reject) => {
        let idToken;
        try {
            idToken = await getIdToken();
        } catch (err) {
            message.error("Failed to remove items from batch")
            reject(err);
        }
        const payload = {
            owner_id: inOwner,
            action: "remove_items_from_batch",
            batch_id: inBatchId,
            data_ids: inDataIds,
        }
        let body: { removed_items: any[] };
        try {
            body = await api_trigger_fxn("data_index", payload);
            
        } catch (e) {
            return reject(e);
        }
        resolve(body);
    })
}

export const removeBatch = (inBatchId: string, inOwner : string) => {
    return new Promise(async (resolve, reject) => {
        let idToken;
        try {
            idToken = await getIdToken();
        } catch (err) {
            message.error("Failed to remove batch")
            reject(err);
        }
        const payload = {
            owner_id: inOwner,
            action: "remove_batch",
            batch_id: inBatchId,
        }
        let body: { removed_items: any[] };
        try {
            body = await api_trigger_fxn("data_index", payload);
        } catch (e) {
            return reject(e);
        }
        resolve(body);
    })
}

export const updateParams = (inBatchId: string, inOwner : string, inParams : object) => {
    return new Promise(async (resolve, reject) => {
        let idToken;
        try {
            idToken = await getIdToken();
        } catch (err) {
            message.error("Failed to update batch params")
            reject(err);
        }
        // TODO: validate batch is locked before updating params

        const payload = {
            owner_id: inOwner,
            action: "update_params",
            batch_id: inBatchId,
            params: inParams,
        }
        let body: { updated_item: {} };
        try {
            body = await api_trigger_fxn("data_index", payload);
        } catch (e) {
            return reject(e);
        }
        resolve(body);
    })
}

export const unlockBatchItem = (inBatchId: string, inOwner : string) => {
    return new Promise(async (resolve, reject) => {
        let idToken;
        try {
            idToken = await getIdToken();
        } catch (err) {
            message.error("Failed to unlock batch")
            reject(err);
        }
        const payload = {
            owner_id: inOwner,
            action: "unlock_batch",
            batch_id: inBatchId,
        }
        let body: { updated_item: {} };
        try {
            body = await api_trigger_fxn("data_index", payload);
        } catch (e) {
            return reject(e);
        }
        resolve(body);
    })
}

export const unlockDataItem = (inDataId: string, inOwner : string) => {
    return new Promise(async (resolve, reject) => {
        let idToken;
        try {
            idToken = await getIdToken();
        } catch (err) {
            message.error("Failed to unlock data item")
            reject(err);
        }
        const payload = {
            owner_id: inOwner,
            action: "unlock_data_item",
            data_id: inDataId,
        }
        let body: { updated_item: {} };
        try {
            body = await api_trigger_fxn("data_index", payload);
        } catch (e) {
            return reject(e);
        }
        resolve(body);
    })
}

export const processBatch = (inBatchId: string, inOwner: string) => {
    return new Promise(async (resolve, reject) => {
        let idToken;
        try {
            idToken = await getIdToken();
        } catch (err) {
            message.error("Failed to process batch")
            reject(err);
        }
        const payload = {
            action: "process",
            owner: inOwner,
            token: idToken,
            batch_id: inBatchId,
        }
        const params = {
            FunctionName: "data_processor_trigger",
            Payload: JSON.stringify(payload)
        };

        window.lambda.invoke(params, async (err: any, resp: any) => {
            handleResponse(err, resp, resolve, reject)
        });
    })
}

export const loadDataIndex = (lastEvaluatedKey: string | null, inOwnerId: string | undefined): Promise<any> => {
    return new Promise(async (resolve, reject) => {
        let payload = {
            action: "query_v3",
            owner_id: inOwnerId,
        }
        if (lastEvaluatedKey) {
            // @ts-ignore
            payload = { ...payload, exclusive_start_key: lastEvaluatedKey }
        }

        let body: { data: any[]; viewable_owners: string[]; viewable_owners_dict: { [x: string]: { name: any } }; last_evaluated_key: any };
        try {
            body = await api_trigger_fxn("data_index", payload);
        } catch (e) {
            // How should we handle load errors?
            return reject(e);
        }

        let data = []
        try {
            data = body.data;
        } catch (e) {
            console.error(e);
            reject(e)
            message.error("Failed to load data index")
            return
        }
        const dataList: DataIndexItem[] = [];

        if (data.length > 0) {
            for (let i = 0; i < data.length; i++) {
                const item = buildDataItem(data[i]);
                dataList.push(item);
            }
        }

        addDataItemsStore(dataList);
        addOwnerIdsStore(body.viewable_owners);
        console.log(body.viewable_owners_dict)

        const ownerDetails : OwnerDetails[] = []
        body.viewable_owners?.forEach((item: string) => {
            ownerDetails.push({
                owner_id: item,
                owner_name: body.viewable_owners_dict[item]?.name
            })
        })
        addOwnerDetailsStore(ownerDetails)

        resolve({
            _ownerIds: body.viewable_owners,
            _lastEvaluatedKey: body.last_evaluated_key,
            _pageSize: data.length,
            _tableData: dataList,
            _ownerDetails: body.viewable_owners_dict
        })



    })

    //Data-index Pagination
};

export const  buildDataItem = (inData : any) : DataIndexItem => {
    const item: any = inData;
    const entwineBuildPath = item.entwine_build_path ? item.entwine_build_path : "";
    const translationV3 = new Vector3();
    const translation = item.translation;
    if (translation) {
        const translationJson = JSON.parse(translation);
        translationV3.x = translationJson.X;
        translationV3.y = translationJson.Y;
        translationV3.z = translationJson.Z;
    }

    let loaded = false;
    let isShow = false;

    const allPointclouds = window.potreeViewer.scene.pointclouds;

    const pointcloud = allPointclouds.find((p: any) => p.entwine_build_path === entwineBuildPath);
    if (pointcloud) {
        loaded = true;

        const pointSourceID = item.id_iterator ? parseInt(item.id_iterator, 10) : -1;
        if (!item.id_iterator) isShow = pointcloud.visible;
        else {
            const uCustomFilterPointSourceIDs = pointcloud.uCustomFilterPointSourceIDs as Matrix4;
            if (uCustomFilterPointSourceIDs.elements.includes(pointSourceID)) {
                isShow = true;
            }
        }

        const indexOfPointSourceId =
            pointcloud.uCustomTranslationPointSourceIDarray.indexOf(pointSourceID);
        if (indexOfPointSourceId >= 0) {
            const mat4: Matrix4 = pointcloud.uCustomTranslationMat4array[indexOfPointSourceId];
            const elements = mat4.elements;
            translationV3.x = elements[12];
            translationV3.y = elements[13];
            translationV3.z = elements[14];
        }
    }
    // console.log(item)
    let data = {
        data_id: item.data_id,
        owner_id: item.owner_id,
        id_iterator: item.id_iterator ? item.id_iterator : "",
        batch_iterator: item.batch_iterator ? item.batch_iterator : "",
        name: item.name ? item.name : "",
        raw_epsg: item.raw_epsg ? item.raw_epsg : "",
        raw_epoch: item.raw_epoch ? item.raw_epoch : "",
        raw_geoid: item.raw_geoid ? item.raw_geoid : "",
        target_epsg: item.target_epsg ? item.target_epsg : "",
        target_epoch: item.target_epoch ? item.target_epoch : "",
        target_geoid: item.target_geoid ? item.target_geoid : "",
        acquisition_date: item.acquisition_date ? item.acquisition_date : "",
        acquisition_type: item.acquisition_type ? item.acquisition_type : "",
        conversion_status: item.conversion_status ? item.conversion_status : ConversionStatus.Normal,
        raw_size: item.raw_size ? (item.raw_size / 1000000000).toFixed(3) : "0",
        entwine_build_path: entwineBuildPath,
        center: item.center ? item.center : "",
        radius: item.radius ? item.radius : "",
        project: item.project ? item.project : "",
        permission_group: item.permission_group ? item.permission_group : "",
        uuid: "",
        las_metadata: item.las_metadata ? item.las_metadata : {},
        show: isShow,
        loaded: loaded,
        isProject: false,
        translationV3: translationV3,
        default_material: item.default_material ? item.default_material : "rgba",
        intensity_min: item.intensity_min ? item.intensity_min : 0,
        intensity_max: item.intensity_max ? item.intensity_max : 5000,
        entwine_epsg: item.entwine_epsg ? item.entwine_epsg : "",
        batch: item.batch ? item.batch : "",
        source_ids: item.source_ids ? item.source_ids : [],
        locked: item.locked ? item.locked : false,
        batch_parent: item.batch_parent ? item.batch_parent : null,
        tags : item.tags ? item.tags : [],
        params: item.params ? item.params : {}
    };
    return data;
}

export const loadDataItem = (inDataId: string, inOwnerId: string): Promise<any> => {
    return new Promise(async (resolve, reject) => {
        let idToken;
        try {
            idToken = await getIdToken();
        } catch (err) {
            message.error("Failed to load data item")
            reject(err);
        }
        const payload = {
            action: "get",
            owner_id: inOwnerId,
            data_id: inDataId,
        }
        let body: { data: any[] };
        try {
            body = await api_trigger_fxn("data_index", payload);
        } catch (e) {
            return reject(e);
        }
        resolve(body);
      
    })
}

export const deleteDataItem = (inDataId: string, inOwnerId: string): Promise<any> => {
    return new Promise(async (resolve, reject) => {
        let idToken;
        try {
            idToken = await getIdToken();
        } catch (err) {
            message.error("Failed to delete data item")
            reject(err);
        }
        const payload = {
            action: "delete",
            owner_id: inOwnerId,
            data_id: inDataId,
        }
        let body: { message: string };
        try {
            body = await api_trigger_fxn("data_index", payload);
        } catch (e) {
            return reject(e);
        }
        resolve(body);
    })
}

export const updateDataItemLambda = (inDataId: string, inOwnerId: string, inParams: object): Promise<any> => {
    return new Promise(async (resolve, reject) => {
        let idToken;
        try {
            idToken = await getIdToken();
        } catch (err) {
            message.error("Failed to update data item")
            reject(err);
        }
        const payload = {
            action: "update",
            data_id: inDataId,
            owner_id: inOwnerId,
            update_dict: { ...inParams },
        };
        let body: { message: string };
        try {
            body = await api_trigger_fxn("data_index", payload);
        } catch (e) {
            return reject(e);
        }
        resolve(body);
    })
}


export const processData = (inDataId: string, inOwner: string) => {
    return new Promise(async (resolve, reject) => {
        let idToken;
        try {
            idToken = await getIdToken();
        } catch (err) {
            message.error("Failed to process batch")
            reject(err);
        }
        const payload = {
            action: "process",
            owner: inOwner,
            token: idToken,
            data_id: inDataId,
        }
        const params = {
            FunctionName: "data_processor_trigger",
            Payload: JSON.stringify(payload)
        };

        window.lambda.invoke(params, async (err: any, resp: any) => {
            handleResponse(err, resp, resolve, reject)
        });
    })
}

export const getS3ForOwner = (ownerId: string): Promise<any> => {
    return new Promise(async (resolve, reject) => {
        let idToken;
        try {
            idToken = await getIdToken();
        } catch (err) {
            reject(err);
        }
        const payload = {
            token: idToken,
            owner_id: ownerId,
        }
        const params = {
            FunctionName: "lambda-permission-requestor",
            Payload: JSON.stringify(payload),
        };

        console.log("triggering permission lambda")
        window.lambda.invoke(params, (err, resp: any) => {
            handleResponse(err, resp, resolve, reject)
        });
    })
}